// Shared booking room / pairing helpers. Loaded in the browser booking bundle
// and require()'d by the server (and tests) via module.exports.
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    // CommonJS (server / tests): export only, don't touch the global.
    module.exports = api;
    return;
  }
  root.EM = root.EM || {};
  root.EM.booking = root.EM.booking || {};
  root.EM.booking.resolve = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  function eventIdsInclude(eventIds, eventId) {
    if (!eventIds || !eventIds.length) return false;
    var target = parseInt(eventId, 10);
    for (var i = 0; i < eventIds.length; i++) {
      if (parseInt(eventIds[i], 10) === target) return true;
    }
    return false;
  }

  function findFirst(list, predicate) {
    if (!list || !list.length) return null;
    for (var i = 0; i < list.length; i++) {
      if (predicate(list[i])) return list[i];
    }
    return null;
  }

  /**
   * Group size limits for a timeslot. Prefer values stamped on the slot during
   * event-variant merge so Family Hour capacity isn't judged against a Regular
   * sibling's maxGroupSize.
   */
  function resolveSlotGroupSize(slot, reservationSlot) {
    var fallback = reservationSlot || {};
    var hasOwnMax = slot && slot.maxGroupSize != null;
    var maxRaw = hasOwnMax ? slot.maxGroupSize : fallback.maxGroupSize;
    var minRaw = slot && slot.minGroupSize != null ? slot.minGroupSize : fallback.minGroupSize;
    // After a merge, `reservationSlot` is the Regular base event. Its `size`
    // (e.g. 8) must not become Family Hour capacity — empty FH slots report
    // free=6, and free <= 8 - privateRoomPlayers marks them sold out.
    var sizeRaw;
    if (slot && slot.size != null) {
      sizeRaw = slot.size;
    } else if (!hasOwnMax) {
      sizeRaw = fallback.size;
    }
    var maxGroupSize = parseInt(maxRaw, 10);
    var size = parseInt(sizeRaw, 10);
    // Fail-safe: `free` is counted against slot size, which can be lower than
    // maxGroupSize. Judging leftover private capacity against the higher max
    // would mark an empty slot as already full.
    var capacity = Number.isFinite(size) && size > 0 ? size : maxGroupSize;
    return {
      maxGroupSize: maxGroupSize,
      minGroupSize: parseInt(minRaw, 10),
      capacity: capacity,
    };
  }

  /**
   * Guests already booked on a slot. IsleHelp counts `free` against slot
   * size (capacity), which can be lower than maxGroupSize — subtracting from
   * max would invent phantom guests on an empty slot. Returns NaN when the
   * inputs are unusable so callers can decide whether to fail open or closed.
   */
  function resolveExistingPlayers(slot, reservationSlot, free) {
    var capacity = resolveSlotGroupSize(slot, reservationSlot).capacity;
    var freeSeats = parseInt(free, 10);
    if (!Number.isFinite(capacity) || !Number.isFinite(freeSeats)) {
      return NaN;
    }
    return Math.max(0, capacity - freeSeats);
  }

  /**
   * Private-room player threshold used by the calendar unavailable check.
   * Mirrors server resolvePrivateRoomPlayers (controllers/booking.js), which
   * takes an event and reads event.slot.maxGroupSize; this takes the resolved
   * maxGroupSize directly. Named differently to avoid confusing the two.
   */
  function resolvePrivateThreshold(room, maxGroupSize) {
    if (room && room.privateRoomPlayers) {
      return room.privateRoomPlayers;
    }
    var privateRoomSize = maxGroupSize > 6 ? 2 : 1;
    return maxGroupSize - privateRoomSize;
  }

  /**
   * Whether a slot should render as unavailable on the booking calendar.
   * Uses the slot's own max/min when present (merged variants).
   *
   * Private rooms (the default) close once booked players reach the private
   * threshold, which hides the last 1–2 seats on a larger room. A room with
   * private explicitly false stays open until free seats are gone, or fewer
   * remain than minGroupSize. Checkout already skips the private hold in
   * that case (cart privateCapable); the calendar has to match it.
   */
  function isSlotUnavailable(slot, reservationSlot, room) {
    var sizes = resolveSlotGroupSize(slot, reservationSlot);
    var free = parseInt(slot && slot.free, 10);
    if (!Number.isFinite(free) || !Number.isFinite(sizes.capacity)) {
      return true;
    }
    var privateCapable = !room || room.private !== false;
    if (privateCapable) {
      var privateRoomPlayers = resolvePrivateThreshold(room, sizes.capacity);
      if (free <= sizes.capacity - privateRoomPlayers) {
        return true;
      }
    } else if (free <= 0) {
      // Explicit so a sold-out slot still closes when minGroupSize is NaN.
      return true;
    }
    if (Number.isFinite(sizes.minGroupSize) && free < sizes.minGroupSize) {
      return true;
    }
    return false;
  }

  /**
   * Whether checkout/book UI should show the "paired with other guests" notice.
   * Occupied slots always qualify unless a non-private room opts out
   * (showPairingMessage === false). Private rooms cannot opt out.
   */
  function shouldShowPairingNotice(room, slotOrders) {
    var orders = parseInt(slotOrders, 10);
    if (!Number.isFinite(orders) || orders <= 0) return false;
    if (room && room.private === false && room.showPairingMessage === false) {
      return false;
    }
    return true;
  }

  /**
   * The eventId every per-variant lookup should key off. On merged rooms the
   * calendar stamps each slot with its own source event (data-event), which
   * lands in state.event; fall back to the merge-base reservation when it is
   * absent or unparseable. Name, custom fields and minPlayersFixedPrice must
   * all resolve from the same id, or a slot gets one variant's label with
   * another's prompts.
   */
  function resolveSelectedEventId(stateEvent, event) {
    var parsed = parseInt(stateEvent, 10);
    if (Number.isFinite(parsed) && parsed !== 0) {
      return parsed;
    }
    var baseId = event && event.reservation ? parseInt(event.reservation.id, 10) : NaN;
    return Number.isFinite(baseId) ? baseId : null;
  }

  /**
   * Whether per-variant data should be read off eventPrices[] at all. Only
   * merged rooms have the array (see buildMergedEventPrice in
   * controllers/booking.js), and an unusable eventId can't select an entry.
   * Callers need this separately from findEventPrice: "not merged" and "merged
   * but no entry for this variant" want different fallbacks.
   */
  function canUseVariantPrices(event, eventId) {
    if (!event || !Array.isArray(event.eventPrices) || event.eventPrices.length === 0) {
      return false;
    }
    return Number.isFinite(parseInt(eventId, 10));
  }

  /** The eventPrices[] entry belonging to eventId, or null. */
  function findEventPrice(event, eventId) {
    if (!canUseVariantPrices(event, eventId)) {
      return null;
    }
    var target = parseInt(eventId, 10);
    for (var i = 0; i < event.eventPrices.length; i++) {
      var ep = event.eventPrices[i];
      if (ep && parseInt(ep.eventId, 10) === target) return ep;
    }
    return null;
  }

  /**
   * IsleHelp/VenueMenu custom fields for the selected reservation event.
   * Merged rooms carry per-eventId customs on eventPrices[]; non-merged
   * events keep them on reservation.custom. Looking only at the merge base
   * reservation leaks Regular fields (e.g. Age Level) onto Family Hour slots.
   */
  function resolveReservationCustom(event, eventId) {
    if (!canUseVariantPrices(event, eventId)) {
      return event && event.reservation && Array.isArray(event.reservation.custom) ? event.reservation.custom : [];
    }
    var ep = findEventPrice(event, eventId);
    if (!ep) {
      // Selected variant missing from eventPrices — do not fall back to the
      // merge-base customs (wrong Age Level / Difficulty prompts).
      return [];
    }
    return Array.isArray(ep.custom) ? ep.custom : [];
  }

  /**
   * Display name for the selected reservation event.
   * Merged rooms keep each variant's cleaned IsleHelp name on eventPrices[];
   * reservation.name is only the merge base and would show e.g. "Atlantis Junior"
   * for a Family Hour slot. Unlike resolveReservationCustom this does fall back
   * to the merge-base name when the variant entry is missing: a slightly
   * generic label is harmless, the wrong custom-field prompts are not.
   */
  function resolveReservationName(event, eventId) {
    var baseName = (event && event.reservation && event.reservation.name) || "";
    var ep = findEventPrice(event, eventId);
    return (ep && ep.name) || baseName;
  }

  /**
   * Resolve a room/table from the client bookingLocations payload for an
   * IsleHelp eventId.
   *
   * When several entries share an eventId we pick the one the calendar
   * actually surfaces. This is NOT the same order the server's addToCart uses
   * (that path prefers event/location-event rooms over tables and discards
   * hidden rooms) — in practice an eventId maps to a single entry, so this
   * only acts as a tiebreaker.
   *
   * Default order (general calendar, lounge, kiosk):
   *   1. tables
   *   2. showInGeneralCalendar event rooms
   *   3. non-hidden permanent rooms
   *   4. any other event room
   *   5. any remaining match (including hidden permanent — avoids null
   *      lookups when a slot is still somehow present client-side)
   *
   * Location-event pages (`opts.preferEventRooms`) mirror the server's
   * processResponse, which checks the location event's rooms first:
   *   0a. event room belonging to this page's event (`opts.eventName`)
   *   0b. any event room, when no eventName is known
   *   then steps 1-5 above.
   * This keeps a colliding IsleHelp eventId (e.g. Darts and Radioactive both
   * using 10) from stealing the event room, and keeps an unrelated location
   * event at the same venue from stealing it when eventName is provided.
   */
  function resolveRoomByEventId(rooms, tables, eventId, opts) {
    var match = function (entry) {
      return entry && eventIdsInclude(entry.eventIds, eventId);
    };

    var list = rooms || [];
    var eventRoom = findFirst(list, function (room) {
      return match(room) && room.roomSource === "event";
    });
    if (opts && opts.preferEventRooms) {
      if (opts.eventName) {
        var ownEventRoom = findFirst(list, function (room) {
          return match(room) && room.roomSource === "event" && room.eventName === opts.eventName;
        });
        if (ownEventRoom) return ownEventRoom;
      } else if (eventRoom) {
        return eventRoom;
      }
    }

    var table = findFirst(tables, match);
    if (table) return table;

    var general = findFirst(list, function (room) {
      return match(room) && room.showInGeneralCalendar === true;
    });
    if (general) return general;

    var permanent = findFirst(list, function (room) {
      return match(room) && room.roomSource === "permanent" && !room.hidden;
    });
    if (permanent) return permanent;

    if (eventRoom) return eventRoom;

    return findFirst(list, match);
  }

  return {
    shouldShowPairingNotice: shouldShowPairingNotice,
    resolveRoomByEventId: resolveRoomByEventId,
    resolveSlotGroupSize: resolveSlotGroupSize,
    resolveExistingPlayers: resolveExistingPlayers,
    resolvePrivateThreshold: resolvePrivateThreshold,
    isSlotUnavailable: isSlotUnavailable,
    resolveSelectedEventId: resolveSelectedEventId,
    resolveReservationCustom: resolveReservationCustom,
    resolveReservationName: resolveReservationName,
  };
});
